import type { GeneratedGHGInventoryReport, ReportClaim, PublicationStatus } from './types';
import { formatPerCapitaMt, formatSharePercent, formatWholeMt, percentChange } from './formatters';
import { findingLabelForSector, HISTORICAL_WARNING, TWO_LENS_COPY } from './memberLanguage';
import {
  hasUnusualResidentDenominator,
  isAgriculturePlaceholderZero,
  sectorShare,
  transportBuildingShares,
} from './sectorPresentation';

function claim(
  partial: Omit<ReportClaim, 'eligibilityStatus' | 'qualityStatus'> & {
    eligibilityStatus?: string;
    qualityStatus?: PublicationStatus;
  }
): ReportClaim {
  return {
    eligibilityStatus: 'eligible',
    qualityStatus: 'provisional',
    ...partial,
  };
}

export function buildClaims(report: GeneratedGHGInventoryReport): ReportClaim[] {
  const claims: ReportClaim[] = [];
  const j = report.identity.jurisdiction;
  const year = report.identity.inventoryYear;
  const ready = report.readiness.reportReady;
  const pop = report.communityProfile.population?.value;
  const density = report.communityProfile.populationDensity?.value;
  const total = report.sectorSummary.totalEmissionsMt.value;
  const perCapita = report.sectorSummary.perCapitaMt.value;

  if (typeof pop === 'number') {
    claims.push(
      claim({
        claimId: 'profile_population',
        claimType: 'population_fact',
        renderedText: `${j.canonicalName} had ${pop.toLocaleString('en-US')} residents in ${year}.`,
        inputFields: { population: pop, year },
        sourceRefs: ['population.population'],
        calculationRule: 'direct read for inventory year',
      })
    );
  } else {
    claims.push(
      claim({
        claimId: 'profile_population_missing',
        claimType: 'missing_data',
        renderedText: `Resident population for ${year} is unavailable, so per-person emissions are not shown.`,
        inputFields: { year },
        sourceRefs: ['population.population'],
        calculationRule: 'missing population suppresses derived metrics',
        eligibilityStatus: 'ineligible',
      })
    );
  }

  if (typeof density === 'number' && Number.isFinite(density)) {
    claims.push(
      claim({
        claimId: 'profile_density',
        claimType: 'density_fact',
        renderedText: `Population density in ${year} was ${Math.round(density).toLocaleString('en-US')} residents per square mile.`,
        inputFields: { density, year },
        sourceRefs: ['population_density.population_density'],
        calculationRule: 'direct read for inventory year',
      })
    );
  }

  if (hasUnusualResidentDenominator(typeof pop === 'number' ? pop : null)) {
    claims.push(
      claim({
        claimId: 'unusual_denominator',
        claimType: 'unusual_denominator',
        renderedText:
          'Resident population is very small, so per-resident emissions are an unusual denominator and are not a typical community intensity measure.',
        inputFields: { population: typeof pop === 'number' ? pop : null, year },
        sourceRefs: ['population.population'],
        calculationRule: 'resident population below 1,000',
      })
    );
  }

  if (typeof total === 'number') {
    claims.push(
      claim({
        claimId: 'inventory_total',
        claimType: 'inventory_total',
        renderedText: `${j.canonicalName}’s ${year} community inventory total is ${formatWholeMt(total)} metric tons of carbon dioxide equivalent.`,
        inputFields: { total, year },
        sourceRefs: ['fetchGHGInventoryData'],
        calculationRule: 'live inventory total for inventory year',
      })
    );
  }

  if (typeof perCapita === 'number' && !hasUnusualResidentDenominator(typeof pop === 'number' ? pop : null)) {
    claims.push(
      claim({
        claimId: 'per_capita',
        claimType: 'per_capita_position',
        renderedText: `That is ${formatPerCapitaMt(perCapita)} metric tons of carbon dioxide equivalent per resident.`,
        inputFields: { perCapita, year },
        sourceRefs: ['derived total / population'],
        calculationRule: 'inventory total / population',
      })
    );
  }

  const dominant = report.sectorSummary.dominantSector;
  if (dominant && typeof total === 'number' && total > 0) {
    const row = report.sectorDetail.find((s) => s.sectorLabel === dominant);
    const share = sectorShare(row?.emissionsMt.value ?? null, total);
    const label = findingLabelForSector(row?.sectorCode, dominant);
    claims.push(
      claim({
        claimId: 'dominant_sector',
        claimType: 'leading_emissions_sector',
        renderedText:
          share != null
            ? `${label} is the largest included category, representing ${formatSharePercent(share)} of the total.`
            : `${label} is the largest included category.`,
        inputFields: { sector: dominant, year, share: share != null ? share : null },
        sourceRefs: ['fetchGHGInventoryData sector series'],
        calculationRule: 'max among non-missing included sector emissions',
      })
    );
  }

  const bt = transportBuildingShares(report.sectorDetail, total ?? null);
  if (bt.transportShare != null && bt.buildingShare != null) {
    const transportLed = (bt.transportMt ?? 0) > (bt.buildingMt ?? 0);
    claims.push(
      claim({
        claimId: 'building_transport_balance',
        claimType: 'building_transport_balance',
        renderedText: transportLed
          ? `On-road transportation represents ${formatSharePercent(bt.transportShare)} of included emissions, compared with ${formatSharePercent(bt.buildingShare)} from building energy.`
          : `Building energy represents ${formatSharePercent(bt.buildingShare)} of included emissions, compared with ${formatSharePercent(bt.transportShare)} from on-road transportation.`,
        inputFields: {
          transportShare: bt.transportShare,
          buildingShare: bt.buildingShare,
          year,
        },
        sourceRefs: ['fetchGHGInventoryData grouped presentation'],
        calculationRule: 'sum of already-calculated LDV+HDV vs four building sectors',
      })
    );
  }

  if (report.historical.displayAllowed) {
    const pts = report.historical.points.filter((p) => p.totalEmissionsMt.value != null);
    const y2005 = pts.find((p) => p.year === 2005);
    const yInv = pts.find((p) => p.year === year);
    if (y2005?.totalEmissionsMt.value != null && yInv?.totalEmissionsMt.value != null) {
      const delta = percentChange(y2005.totalEmissionsMt.value, yInv.totalEmissionsMt.value);
      if (delta != null) {
        const magnitude = Math.abs(delta).toFixed(1);
        const direction = delta < 0 ? 'lower' : delta > 0 ? 'higher' : 'unchanged';
        const changeClause =
          delta === 0
            ? `unchanged from 2005 to ${year}`
            : `${magnitude}% ${direction} in ${year} than in 2005`;
        claims.push(
          claim({
            claimId: 'historical_change',
            claimType: 'trend_descriptive',
            renderedText: `The descriptive software series is ${changeClause}. ${HISTORICAL_WARNING}`,
            inputFields: { from: y2005.totalEmissionsMt.value, to: yInv.totalEmissionsMt.value, year },
            sourceRefs: ['fetchGHGInventoryData historical stack'],
            calculationRule: '(inventory year − 2005) / 2005 using the live software series',
            qualityStatus: 'descriptive_only',
          })
        );
      }
    }
  } else {
    claims.push(
      claim({
        claimId: 'historical_suppressed',
        claimType: 'historical_availability',
        renderedText: report.historical.comparabilityLabel,
        inputFields: { pointCount: report.historical.points.length },
        sourceRefs: ['fetchGHGInventoryData historical stack'],
        calculationRule: 'require >= 2 years for descriptive trend',
        eligibilityStatus: report.historical.points.length >= 2 ? 'eligible' : 'ineligible',
        qualityStatus: 'descriptive_only',
      })
    );
  }

  const cbei = report.consumption;
  if (cbei && !cbei.suppressed && cbei.categories.length) {
    const top = [...cbei.categories].sort(
      (a, b) => (b.displayTotalMt.value ?? 0) - (a.displayTotalMt.value ?? 0)
    )[0];
    if (top?.displayTotalMt.value != null && cbei.totalMt.value != null && cbei.totalMt.value > 0) {
      claims.push(
        claim({
          claimId: 'cbei_top_category',
          claimType: 'cbei_pattern',
          renderedText: `In the household consumption inventory, ${top.categoryLabel.toLowerCase()} represents the largest share (${formatSharePercent(top.displayTotalMt.value / cbei.totalMt.value)}).`,
          inputFields: { category: top.categoryLabel, year },
          sourceRefs: ['cbei_cities scaled categories'],
          calculationRule: 'argmax of scaled CBEI categories',
        })
      );
    }
  }

  claims.push(
    claim({
      claimId: 'two_lens_scope',
      claimType: 'scope_explanation',
      renderedText: TWO_LENS_COPY,
      inputFields: {},
      sourceRefs: ['ClimatePlans report specification'],
      calculationRule: 'static template',
    })
  );

  if (isAgriculturePlaceholderZero(report.sectorDetail)) {
    claims.push(
      claim({
        claimId: 'agriculture_placeholder',
        claimType: 'quality_limitation',
        renderedText: 'Agriculture is not shown as a separate source when the current calculation contains only a placeholder zero.',
        inputFields: { year },
        sourceRefs: ['fetchGHGInventoryData Agriculture series'],
        calculationRule: 'agriculture value is 0 or missing',
      })
    );
  }

  claims.push(
    claim({
      claimId: 'water_not_modeled',
      claimType: 'quality_limitation',
      renderedText:
        'On the GHG inventory page, landfill waste and wastewater are combined as Waste & wastewater. Wastewater is also shown as a separate ClimatePlans observation, year-matched or scaled from the stored inventory year.',
      inputFields: { year },
      sourceRefs: ['inventory model coverage'],
      calculationRule: 'static not_modeled sector',
    })
  );

  if (!ready) {
    for (const reason of report.readiness.failureReasons) {
      claims.push(
        claim({
          claimId: `not_ready_${reason.replace(/\W+/g, '_').slice(0, 40)}`,
          claimType: 'missing_data',
          renderedText: reason,
          inputFields: { inventoryYear: year },
          sourceRefs: ['report readiness gates'],
          calculationRule: 'live inventory presence',
          eligibilityStatus: 'ineligible',
          qualityStatus: 'blocked',
        })
      );
    }
  }

  return claims;
}

const FINDING_TYPES = new Set([
  'leading_emissions_sector',
  'building_transport_balance',
  'trend_descriptive',
  'cbei_pattern',
]);

export function memberFacingFindings(report: GeneratedGHGInventoryReport, limit = 4): string[] {
  return report.claims
    .filter((c) => c.eligibilityStatus === 'eligible' && FINDING_TYPES.has(c.claimType))
    .map((c) => c.renderedText)
    .slice(0, limit);
}
